<template>
    <div class="emoji-picker | relative">
        <div class="h-full" ref="trigger" @click="onClick">
            <slot name="trigger" />
        </div>
        <nio-popup
            :opened="isOpen"
            placement="top"
            :trigger="$refs.trigger"
            @clickin="onClickIn"
            @clickout="onClickOut"
        >
            <nav class="pt-1 px-3.5 flex border-b border-gray-300">
                <button
                    class="flex items-center justify-center text-xl border-b-2 flex-1"
                    :class="activeGroup === group.name ? 'text-blue-500 border-blue-500' : 'border-transparent filter-grayscale hover:border-blue-500'"
                    :key="group.name"
                    @click="onCategoryClick(group.name)"
                    v-html="group.icon.output"
                    v-for="group in groups"
                />
            </nav>
            <div class="max-h-80 w-76 overflow-y-auto" ref="scroll">
                <div
                    class="px-2 pt-3 mb-3"
                    :data-group="group.name"
                    :key="group.name"
                    ref="groups"
                    v-for="group in groups"
                >
                    <div class="px-1.5 font-semibold whitespace-no-wrap" v-text="group.translation" />
                    <div class="flex flex-wrap">
                        <button
                            class="inline-flex items-center justify-center text-2xl w-9 h-9 rounded-full bg-white hover:bg-gray-200"
                            :key="emoji.hexcode"
                            @click="onSelect(emoji)"
                            v-for="emoji in group.emojis"
                        >
                            <span class="inline-flex items-center justify-center" v-html="emoji.output" v-if="rendered.includes(group.name)" />
                        </button>
                    </div>
                </div>
            </div>
        </nio-popup>
    </div>
</template>

<script>
    import orderBy from 'lodash/orderBy.js';
    import { elementContains } from '@/utils/dom.js';
    import { unicodeToOutput } from '@/utils/emoji.js';
    import emojiGroups from 'emojibase-data/meta/groups.json';
    import emojis from 'emojibase-data/en/data.json';

    const groupSettings = {
        'smileys-people': {
            icon: '1F603',
            order: 1,
        },
        'animals-nature': {
            icon: '1F435',
            order: 2,
        },
        'food-drink': {
            icon: '1F354',
            order: 3,
        },
        'travel-places': {
            icon: '1F698',
            order: 5,
        },
        'activities': {
            icon: '26BD',
            order: 4,
        },
        'objects': {
            icon: '1F4A1',
            order: 6,
        },
        'symbols': {
            icon: '1F523',
            order: 7,
        },
        'flags': {
            icon: '1F6A9',
            order: 8,
        },
    };
    const groupedEmojis = Object.values(emojiGroups.groups).map(group => ({
        name: group,
        emojis: [],
    }));

    emojis.sort((a, b) => a.order - b.order);
    emojis.forEach(emoji => {
        const output = unicodeToOutput(emoji.emoji);
        const isValid = typeof emoji.group !== 'undefined';

        if (isValid) {
            groupedEmojis[emoji.group].emojis.push({
                ...emoji,
                output,
            });
        }
    });

    export default {
        props: {
            closeOnSelect: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                isOpen: false,
                activeGroup: 'smileys-emotion',
                lastActiveGroup: '',
                scrollObserver: null,
                rendered: [],
            };
        },

        computed: {
            groups() {
                const filteredGroupedEmojis = [
                    {
                        name: 'smileys-people',
                        emojis: [],
                    },
                ];

                groupedEmojis.forEach(group => {
                    if (['component'].includes(group.name)) {
                        return;
                    }

                    if (['smileys-emotion', 'people-body'].includes(group.name)) {
                        filteredGroupedEmojis[0].emojis.push(...group.emojis);
                        return;
                    }

                    filteredGroupedEmojis.push(group);
                });

                return orderBy(filteredGroupedEmojis.map(group => {
                    const icon = group.emojis.find(emoji => emoji.hexcode === groupSettings[group.name].icon);

                    return {
                        ...group,
                        translation: this.$t(`emojis.groups.${group.name}`),
                        icon,
                        order: groupSettings[group.name].order,
                    };
                }), 'order');
            },
        },

        watch: {
            activeGroup(newValue, oldValue) {
                this.lastActiveGroup = oldValue;
                this.renderGroup(newValue);
            },
        },

        methods: {
            renderGroup(group) {
                if (!this.rendered.includes(group)) {
                    this.rendered.push(group);
                }
            },

            open() {
                this.renderGroup(this.activeGroup);
                this.isOpen = true;
            },

            close() {
                this.isOpen = false;
            },

            onClick() {
                if (this.isOpen) {
                    this.close();
                } else {
                    this.open();
                }
            },

            onSelect(emoji) {
                this.$emit('select', emoji.emoji);
            },

            onClickOut(e) {
                if (!elementContains(this.$refs.trigger, e.target)) {
                    this.close();
                }
            },

            onClickIn() {
                if (this.closeOnSelect) {
                    this.close();
                }
            },

            onCategoryClick(category) {
                this.lastActiveGroup = '';
                this.activeGroup = category;

                this.$refs.groups.forEach(group => {
                    if (group.dataset.group === category) {
                        group.scrollIntoView(true);
                    }
                });
            },
        },

        mounted() {
            this.scrollObserver = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.activeGroup = entry.target.dataset.group;
                    } else if (this.activeGroup == entry.target.dataset.group) {
                        this.activeGroup = this.lastActiveGroup;
                    }
                });
            }, { root: this.$refs.scroll });

            this.$refs.groups.forEach(group => {
                this.scrollObserver.observe(group);
            });
        },

        beforeDestroy() {
            this.scrollObserver.disconnect();
            this.scrollObserver = null;
        },

        i18n: {
            messages: {
                en: {
                    emojis: {
                        groups: {
                            'smileys-people': 'Smileys & People',
                            'animals-nature': 'Animals & Nature',
                            'food-drink': 'Food & Drink',
                            'travel-places': 'Travel & Places',
                            'activities': 'Activities',
                            'objects': 'Objects',
                            'symbols': 'Symbols & Signs',
                            'flags': 'Flags',
                        },
                    },
                },
                fr: {
                    emojis: {
                        groups: {
                            'smileys-people': 'Smileys & Personnes',
                            'animals-nature': 'Animaux & Nature',
                            'food-drink': 'Nourriture & Boissons',
                            'travel-places': 'Voyages & Lieux',
                            'activities': 'Activités',
                            'objects': 'Objets',
                            'symbols': 'Symboles & Signes',
                            'flags': 'Drapeaux',
                        },
                    },
                },
            },
        },
    };
</script>

<style>
    .emoji-picker img.emoji {
        font-size: 1em;
        margin: 0;
        vertical-align: baseline;
    }
</style>
